import React, { useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Start writing...', 
  height = '200px',
  className = '',
  readOnly = false 
}) => {
  // Configure toolbar modules for rich text editing
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      ['link', 'image'],
      ['clean']
    ],
  }), []);

  // Configure formats to preserve source formatting
  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'indent',
    'link', 'image', 'video',
    'color', 'background',
    'align', 'code-block'
  ];

  const editorStyle = {
    height: height,
    marginBottom: '42px', // Space for toolbar
  };

  return (
    <div className={`rich-text-editor ${className}`}>
      <style jsx global>{`
        .ql-toolbar {
          border: 1px solid #e5e7eb;
          border-bottom: none;
          border-radius: 8px 8px 0 0;
          background: #fafafa;
        }
        
        .ql-container {
          border: 1px solid #e5e7eb;
          border-radius: 0 0 8px 8px;
          font-family: inherit;
        }
        
        .ql-editor {
          min-height: ${height};
          font-family: inherit;
          font-size: 14px;
          line-height: 1.6;
        }
        
        .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        
        .ql-snow .ql-picker {
          color: #374151;
        }
        
        .ql-snow .ql-stroke {
          stroke: #6b7280;
        }
        
        .ql-snow .ql-fill {
          fill: #6b7280;
        }
        
        .ql-snow .ql-picker-options {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        
        .ql-snow .ql-tooltip {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        
        .ql-snow .ql-tooltip input[type=text] {
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          padding: 4px 8px;
        }
        
        .ql-snow .ql-tooltip a.ql-action::after {
          content: 'Edit';
          color: #3b82f6;
        }
        
        .ql-snow .ql-tooltip a.ql-remove::before {
          content: 'Remove';
          color: #ef4444;
        }
        
        /* Custom styling for better integration */
        .rich-text-editor .ql-toolbar {
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }
        
        .rich-text-editor .ql-container {
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }
        
        /* Focus states */
        .rich-text-editor .ql-container:focus-within {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }
        
        .rich-text-editor .ql-toolbar:focus-within {
          border-color: #3b82f6;
        }
      `}</style>
      
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly}
        style={editorStyle}
      />
    </div>
  );
};

export default RichTextEditor;